// 鶏 (inverted-jester・終わらぬ鬨) の「弾くほど加速」(2026-10-25・台帳 docs/ledger/story.md 10-24 の 26)。
//   11-04 から配布版は「焦り狂い」(src/rooster_frenzy.ts) に置き換わり、ここが動くのは開発版 `?roosterFrenzy=off` の時だけ。
//   下の「既定 (配布版)」は ?roosterFrenzy=off の中での既定のこと。?roosterRush= と ?roosterRushCue= は比較のために消さずに残す。
//   作者の言葉「弾くにつれて加速してくのは面白いから採用してみよう」。
//   数値は試作 (Artifact M8XiFLWNvKNcysqi72eb5A・docs/research/2026-10-24-rooster-catch-up-prototype.md) のまま。
//
//   鶏は時計で動く。弾かれる (+350ms)・殴られてひるむ (+250ms・開いた隙ごとに最初の1発だけ) と、時計から遅れる。
//   遅れは戻りと待機を詰めて取り戻す (次の連が早く来る)。下限は 戻り 450/700。取り戻しきれない分は持ち越す。
//   鳴きの長さ (溜め) と爪と爪の間は詰めない。鳴き声の形が爪の拍で、聞いた拍のまま弾けるように (10-30 R-1
//   「鳴き声によって弾くリズムが異なる感じに」・src/rooster_toki.ts)。間を詰めると弾き直しの床 320ms も割っていた。
//
//   DEV `?roosterRush=on`  = 案A (推奨): 体幹の代わりに遅れのゲージ。体幹のゲージ欄に遅れを映し、
//                           次の連の頭で遅れが 1600ms 以上なら崩れる (崩れは 2600ms)。弾きでは体幹は溜まらない。
//   既定 (配布版) = 案B (10-25 作者「案B」): 体幹はいまのまま。加速だけ足す (遅れは 1600ms で頭打ち・遅れでは崩れない)。
//   どちらも、加速した連の頭で短い「カッ」(rooster_rush_tick) を鳴らし、遅れに比例して体が小刻みに震える。
//   DEV `?roosterRush=off` = 加速なし (10-25 より前の配布版)。
//   合図の比較 (11-03・作者「弾かれるたびに震えてるその意図が…わからなかった」・docs/research/2026-11-03-rooster-rush-cue.md):
//   既定 (配布版) = jitter (今の震え)。DEV `?roosterRushCue=fluff` = 案A 震えを外し、弾くたびに綿毛が抜け、急いた連の頭でぶわっと散る
//   (src/rooster_fx.ts drawRoosterRushFluff)・`hurry` = 案B 震えを取り戻している間 (待機・戻り) だけにして大きく遅く・`none` = 案C 合図なし (速さと「カッ」だけ)。
import type { Boss, SfxId } from './types'
import { FIXED_DT } from './balance'
import { regenBossPosture } from './posture'
import { roosterFrenzyMode } from './rooster_frenzy_spec'

export type RoosterRushMode = 'off' | 'on' | 'add'

export interface RoosterRushState {
  /** 時計からの遅れ (ms)。 */
  lagMs: number
  /** ひるみの遅れを数えた隙の印 (同じ隙の2発目以降は数えない)。 */
  flinchKey: string | null
  /** 崩れを 2600ms へ延ばす残り (ms)。 */
  downHoldMs: number
}

export const ROOSTER_RUSH = {
  parryDelayMs: 350,
  hitDelayMs: 250,
  breakAtMs: 1600,
  downMs: 2600,
  /** 崩れの通常の長さ (balance.ts BOSS.staggerMs と同じ)。延ばす分 = downMs - これ。 */
  baseStaggerMs: 1400,
  recoveryMinRatio: 450 / 700,
  /** 待機 (次の技を選ぶまでの間) の速さ。 */
  idleRate: 2,
  /** 加速の「カッ」を鳴らす遅れの下限 (ms)。 */
  tickCueMinLagMs: 150,
  /** 震えの最大 (px)。 */
  jitterMaxPx: 3.2,
  /** 案B (?roosterRushCue=hurry) の震え: 取り戻している間だけ・大きく遅く (px / Hz)。 */
  hurryMaxPx: 6,
  hurryHz: 9,
} as const

const BOSS_ID = 'inverted-jester'
/** 加速するのは鬨ずらしだけ (src/rooster_fight.ts)。縦の技は当たりの時機が台本の時計なので触らない。 */
const RUSH_ATTACKS = new Set(['tokiZurashiShort', 'tokiZurashiLong'])

let modeOverride: RoosterRushMode | null = null
// 10-25 作者「案B」＝配布版の既定は add。案A は ?roosterRush=on、加速なしは ?roosterRush=off
let urlMode: RoosterRushMode = 'add'
if (import.meta.env.DEV && typeof window !== 'undefined') {
  const v = new URLSearchParams(window.location.search).get('roosterRush')
  urlMode = v === 'on' || v === 'a' ? 'on' : v === 'off' ? 'off' : 'add'
}

export type RoosterRushCue = 'jitter' | 'fluff' | 'hurry' | 'none'
let cueOverride: RoosterRushCue | null = null
let urlCue: RoosterRushCue = 'jitter'
if (import.meta.env.DEV && typeof window !== 'undefined') {
  const v = new URLSearchParams(window.location.search).get('roosterRushCue')
  urlCue = v === 'fluff' || v === 'hurry' || v === 'none' ? v : 'jitter'
}
/** テスト用。null で URL の値へ戻す。 */
export function setRoosterRushCue(c: RoosterRushCue | null): void { cueOverride = c }
export function roosterRushCue(): RoosterRushCue { return cueOverride ?? urlCue }

/** テスト用。null で URL の値へ戻す。 */
export function setRoosterRushMode(m: RoosterRushMode | null): void { modeOverride = m }
export function roosterRushMode(): RoosterRushMode { return modeOverride ?? urlMode }

function active(boss: { id: string }): boolean {
  // 焦り狂い (11-04 から配布版・src/rooster_frenzy.ts) の間は加速も震えも止める (11-03 作者の答え: 今の震えは外す)。動くのは開発版 ?roosterFrenzy=off だけ。
  return boss.id === BOSS_ID && roosterRushMode() !== 'off' && roosterFrenzyMode() === 'off'
}

/** 案A では鶏の体幹は弾き・殴りで溜まらない (遅れが代わり)。combat.ts bossPostureGainMul が掛ける。 */
export function roosterRushPostureGainMul(boss: { id: string }): number {
  return active(boss) && roosterRushMode() === 'on' ? 0 : 1
}

function stateOf(boss: Boss): RoosterRushState {
  return boss.roosterRush ?? { lagMs: 0, flinchKey: null, downHoldMs: 0 }
}

/** いまの段の時間の進む速さ (1 = 通常)。遅れがある時だけ 1 を超える。 */
export function roosterRushRate(boss: Boss): number {
  const lag = boss.roosterRush?.lagMs ?? 0
  if (lag <= 0) return 1
  if (boss.state === 'idle') return ROOSTER_RUSH.idleRate
  const atk = boss.currentAttack
  if (!atk) return 1
  if (!RUSH_ATTACKS.has(atk.id)) return 1
  if (boss.state === 'recovery') return 1 / ROOSTER_RUSH.recoveryMinRatio
  return 1
}

/**
 * ボスの更新の前に1回。遅れがあれば段の経過を先へ進め (= 間を詰める)、その分だけ遅れを返す。
 *   崩れの延長中は経過を止める。
 */
export function roosterRushPreTick(boss: Boss): Boss {
  if (!active(boss)) return boss
  const st = stateOf(boss)
  const dtMs = FIXED_DT * 1000
  if (boss.state === 'staggered') {
    if (st.downHoldMs > 0 && boss.stateElapsed * 1000 >= ROOSTER_RUSH.baseStaggerMs / 2) {
      return { ...boss, stateElapsed: boss.stateElapsed - FIXED_DT, roosterRush: { ...st, downHoldMs: Math.max(0, st.downHoldMs - dtMs) } }
    }
    return boss
  }
  const rate = roosterRushRate(boss)
  if (rate <= 1 || st.lagMs <= 0) return boss
  const extraMs = Math.min(st.lagMs, dtMs * (rate - 1))
  return { ...regenFor(boss, extraMs), stateElapsed: boss.stateElapsed + extraMs / 1000, roosterRush: { ...st, lagMs: st.lagMs - extraMs } }
}

/** 体幹の戻り (regenBossPosture・1回 = FIXED_DT) を ms 分だけ進める。端数は1回分を比で混ぜる。 */
function regenFor(boss: Boss, ms: number): Boss {
  const dtMs = FIXED_DT * 1000
  let b = boss
  let left = ms
  while (left >= dtMs) { b = regenBossPosture(b); left -= dtMs }
  if (left > 0) {
    const n = regenBossPosture(b)
    const f = left / dtMs
    b = { ...b, posture: b.posture + (n.posture - b.posture) * f,
      postureRegenLockSec: (b.postureRegenLockSec ?? 0) + ((n.postureRegenLockSec ?? 0) - (b.postureRegenLockSec ?? 0)) * f }
  }
  return b
}

function openingKey(boss: Boss): string | null {
  if (boss.state === 'telegraph' || boss.state === 'recovery' || boss.state === 'idle') return `${boss.attackCount}:${boss.state}`
  return null
}

/**
 * 当たりの解決の後に1回。弾き・ひるみで遅れを足し、案A は遅れを体幹の欄へ映し、連の頭で崩す。
 *   prev = この tick の更新前のボス、next = 当たりの解決後のボス、sfx = この tick に鳴る音。
 *   hitBoss = 主人公の攻撃がこの tick に当たったか。stagger = boss.ts staggerBoss (循環 import を避けて渡す)。
 */
export function roosterRushAfterHits(prev: Boss, next: Boss, sfx: readonly SfxId[], hitBoss: boolean,
  stagger: (b: Boss) => Boss): { boss: Boss; sfx: SfxId[] } {
  if (!active(next)) return { boss: next, sfx: [] }
  const mode = roosterRushMode()
  let st = stateOf(next)
  const out: SfxId[] = []
  if (next.state === 'staggered') {
    // 崩れている間は遅れを数えない。崩れから戻ったら時計を崩れの時点から始め直す (= 遅れ 0)。
    if (st.lagMs !== 0) st = { ...st, lagMs: 0 }
    const b = { ...next, roosterRush: st }
    return { boss: mode === 'on' ? { ...b, posture: 0 } : b, sfx: out }
  }
  if (prev.state === 'staggered') st = { ...st, lagMs: 0, flinchKey: null, downHoldMs: 0 }
  let lag = st.lagMs
  if (sfx.includes('parry')) lag += ROOSTER_RUSH.parryDelayMs
  if (hitBoss) {
    const key = openingKey(prev)
    if (key && key !== st.flinchKey) {
      lag += ROOSTER_RUSH.hitDelayMs
      st = { ...st, flinchKey: key }
    }
  }
  if (mode === 'add') lag = Math.min(lag, ROOSTER_RUSH.breakAtMs)
  st = { ...st, lagMs: lag }
  let boss: Boss = { ...next, roosterRush: st }
  const enteredTelegraph = boss.state === 'telegraph' && prev.state !== 'telegraph'
  if (enteredTelegraph && mode === 'on' && lag >= ROOSTER_RUSH.breakAtMs) {
    boss = stagger(boss)
    boss = { ...boss, posture: 0, roosterRush: { lagMs: 0, flinchKey: null, downHoldMs: ROOSTER_RUSH.downMs - ROOSTER_RUSH.baseStaggerMs } }
    out.push('stagger')
    return { boss, sfx: out }
  }
  if (enteredTelegraph && lag >= ROOSTER_RUSH.tickCueMinLagMs && boss.currentAttack && RUSH_ATTACKS.has(boss.currentAttack.id)) {
    out.push('rooster_rush_tick')
  }
  if (mode === 'on') {
    boss = { ...boss, posture: Math.min(0.97, lag / ROOSTER_RUSH.breakAtMs) * boss.maxPosture }
  }
  return { boss, sfx: out }
}

/** いま遅れを取り戻している (= 段の時間を詰めている) か。 */
export function roosterRushCatchingUp(boss: Boss): boolean {
  return active(boss) && (boss.roosterRush?.lagMs ?? 0) > 0 && roosterRushRate(boss) > 1
}

/**
 * 描画用: 遅れに比例した横の震え (px)。他ボス・崩れ中は 0。
 *   配布版 (jitter) = 遅れがある間ずっと 23Hz・最大 3.2px。hurry = 取り戻している間だけ 9Hz・最大 6px。fluff / none = 0。
 */
export function roosterRushJitterPx(boss: Boss, timeSec: number): number {
  if (!active(boss) || boss.state === 'staggered') return 0
  const lag = boss.roosterRush?.lagMs ?? 0
  if (lag <= 0) return 0
  const cue = roosterRushCue()
  const k = Math.min(1, lag / ROOSTER_RUSH.breakAtMs)
  if (cue === 'jitter') return ROOSTER_RUSH.jitterMaxPx * k * Math.sin(timeSec * 2 * Math.PI * 23)
  if (cue === 'hurry' && roosterRushCatchingUp(boss)) {
    // 小さな遅れでも見えるよう、下限を 0.4 にする。
    return ROOSTER_RUSH.hurryMaxPx * (0.4 + 0.6 * k) * Math.sin(timeSec * 2 * Math.PI * ROOSTER_RUSH.hurryHz)
  }
  return 0
}
